import React, { useEffect, useState } from "react";
import {
	Card,
	Container,
	Row,
	Col,
	Placeholder,
	Button,
	ListGroup,
} from "react-bootstrap";
import LeaderBoardTable from "../../League/Components/LeaderBoardTable";
import {
	Leaderboard,
	LeaderboardTeam,
	RoundWinner,
} from "../../../types/model";
import { useAppSelector } from "../../../store/hooks";
import { RootState } from "../../../store/store";
import SmallLogo from "../../common/SmallLogo";
import PointsBreakdown from "./PointsBreakdown";
import { getRoundWinners } from "../../../services/leaderboardService";
import { IoMdArrowDropright } from "react-icons/io";

const LeagueLeaderboard = () => {
	const { user, isLoggedIn } = useAppSelector((state: RootState) => state.user);
	const {
		selectedLeaderBoard: leaderboard,
		leaderboardLoading,
	} = useAppSelector((state: RootState) => state.fantasy);

	const [roundWinners, setRoundWinners] = useState<RoundWinner[]>([]);

	const [filteredLeaderboard, setFilteredLeaderboard] =
		useState<Leaderboard | null>(leaderboard);

	const [showModal, setShowModal] = useState(false);

	useEffect(() => {
		if (leaderboard) {
			setFilteredLeaderboard(leaderboard);
			fetchRoundWinner();
		}
	}, [leaderboard]);

	const fetchRoundWinner = async () => {
		try {
			const res = await getRoundWinners(leaderboard.leagueId);
			if (res) {
				setRoundWinners(res.data);
			}
		} catch (error) {
			console.error("Error fetching round winner:", error);
		}
	};

	// Find the current user's position
	const userTeam = leaderboard.teams?.find((team) => team.userId === user._id);
	const userPosition =
		[...(leaderboard.teams ?? [])]
			.sort(
				(a: LeaderboardTeam, b: LeaderboardTeam) =>
					b.totalPoints - a.totalPoints,
			)
			.findIndex((team: LeaderboardTeam) => team.userId === user._id) + 1;

	const hasUserStanding = Boolean(userTeam) && isLoggedIn;

	const handleShowPointsBreakdown = () => {
		if (userTeam) {
			setShowModal(true);
		}
	};

	return (
			<Container className="p-0">
				<Row className="mb-3">
					{" "}
					<Col>
						<div className="text-center text-primary fw-bold fs-2 d-flex flex-row justify-content-center align-items-center">
						<SmallLogo />
						<div className="">LIVE LEADERBOARD</div>
					</div>
				</Col>
			</Row>
			{leaderboardLoading ? (
				<Placeholder as="div" animation="glow" className="my-3">
					<Placeholder
						xs={11}
						className="rounded-3"
						bg="light"
						style={{ height: "15rem" }}
					/>
				</Placeholder>
			) : (
					<>
						<Card className="p-3 border-1 border-info bg-dark mb-2">
							<Card className="p-3 border-1 border-info bg-secondary mb-3">
								{hasUserStanding ? (
									<div className="d-flex flex-row justify-content-between text-white align-items-center">
										<div className="fw-bold d-flex flex-row align-items-center fs-5">
											{userTeam?.userName}
											<div className="text-light ms-3">
												<b className="text-primary">#{userPosition || "N/A"}</b>/
												{leaderboard.teams?.length}
											</div>
										</div>
										<div className="text-end fw-bold d-flex align-items-center">
											<span className="text-uppercase small text-light me-2">
												Your Standing
											</span>
											<Button
												size="sm"
												className="fw-bold ms-2"
												onClick={handleShowPointsBreakdown}
											>
												BREAKDOWN
											</Button>
										</div>
									</div>
								) : (
									<div className="d-flex flex-row justify-content-between align-items-center text-white">
										<div className="fw-semibold">Join this league to track your standings.</div>
										<div className="text-light small">
											{leaderboard.teams?.length || 0} teams
										</div>
									</div>
								)}
							</Card>
							<Row className="d-flex flex-row justify-content-between">
							<Col lg={6} md={12} sm={12} xs={12}>
								<div className="fw-bold text-white fs-5 text-start mb-2 mt-2">
									LEADERBOARD
								</div>
							</Col>
							<Col
								lg={6}
								md={12}
								sm={12}
								xs={12}
								className="text-end d-flex justify-content-end"
							>
								<div className="mb-3">
									<input
										type="text"
										className="form-control bg-dark text-white border-info custom-input custom-placeholder rounded-pill"
										placeholder="Search by username..."
										style={{ width: "15rem" }}
										onChange={(e) => {
											const filterValue = e.target.value.toLowerCase();
											const filteredTeams = leaderboard.teams?.filter((team) =>
												team.userName.toLowerCase().includes(filterValue),
											);

											// Create a filtered version of the leaderboard
											const filteredLeaderboard = {
												...leaderboard,
												teams: filteredTeams || [],
											};

											setFilteredLeaderboard(filteredLeaderboard);
										}}
									/>
								</div>
							</Col>
						</Row>
						<LeaderBoardTable
							leaderboard={filteredLeaderboard ?? leaderboard}
							showPointsBreakdown={true}
							showLeagueName={false}
							expanded={true}
						/>
						<Card className="p-3 border-1 border-info bg-dark mb-2 mt-3">
							<div className="fw-bold text-white fs-5 text-start mb-3">
								ROUND WINNERS
							</div>
							{Array.isArray(roundWinners) &&
								roundWinners.length > 0 &&
								roundWinners.map((winner) => (
									<>
										{" "}
										{winner.totalPoints > 1 && (
											<ListGroup.Item
												key={winner._id}
												className="bg-dark text-white border-1 border-info mb-2"
											>
												<div className="d-flex flex-row justify-content-between">
													<div className="">
														🏆 {winner?.eventId.location}:{" "}
														<b>{winner?.userName}</b>{" "}
														<IoMdArrowDropright className="mb-1" />{" "}
														{winner?.totalPoints} points
													</div>
												</div>
											</ListGroup.Item>
										)}
									</>
								))}
						</Card>
					</Card>
				</>
			)}
			<PointsBreakdown
				leaderboardTeam={userTeam ?? ({} as LeaderboardTeam)}
				showModal={showModal}
				handleClose={() => setShowModal(false)}
			/>
		</Container>
	);
};

export default LeagueLeaderboard;
